import React, { useState, useEffect, useRef, useCallback } from 'react'

import 'mapbox-gl/dist/mapbox-gl.css'
import MapboxMap, { type MapMouseEvent, type MapRef, NavigationControl } from 'react-map-gl/mapbox'

import { AssetDetailModal } from '@/components/ui'
import { useCompany, useDashboard, useDocumentTitle } from '@/hooks'
import { addLabelBackgrounds } from '@/lib/map/markers'

import { AssetDetailSheet } from './asset-detail-sheet/AssetDetailSheet'
import { AssetPanel } from './AssetPanel'
import { AssetQuickView } from './AssetQuickView'
import BottomNavMobileControls from './BottomNavMobileControls'
import { Legend } from './Legend'
import { defaultSidebarState, Filters, useMapContext } from './MapContext'
import { MapLayers } from './MapLayers'
import { MapLayerToggles } from './MapLayerToggles'
import { MapStyleToggles } from './MapStyleToggles'
import { MapToolbar } from './MapToolbar'
// import { TopNavMapControlsMobile } from './TopNavMapControlsMobile'
import { addMarkerImages, getCenterFromBounds } from './utils'

// Get Mapbox token from environment or use a placeholder
const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN

/** The open detail view lives in the hash (`#asset=12345&company=678`) so its URL reopens it. */
type AssetLink = { assetId: number; companyId: number }

function parseAssetLink(hash: string): AssetLink | undefined {
  const params = new URLSearchParams(hash.replace(/^#/, ''))
  const assetId = Number(params.get('asset'))
  const companyId = Number(params.get('company'))
  return assetId > 0 && companyId > 0 ? { assetId, companyId } : undefined
}

function formatAssetLink(link: AssetLink | undefined) {
  return link ? `#asset=${link.assetId}&company=${link.companyId}` : ''
}

export const AssetMap: React.FC = () => {
  const context = useMapContext()
  const { setSelectedSavedMap, assetDetailSheetIsOpen, setAssetDetailSheetIsOpen } = context
  const layoutMode = context.sidebarState.layoutMode
  const splitRatio = context.sidebarState.splitRatio
  const selectedAssetName =
    context.currentAssetData?.name ?? context.allAssets.find((a) => a.id === context.currentAssetId)?.name
  useDocumentTitle(selectedAssetName ? `${selectedAssetName} — Assets` : 'Assets')
  const [activeFilter, setActiveFilter] = useState<keyof Filters | undefined>()
  const [isResizing, setIsResizing] = useState(false)
  const [isSplitResizing, setIsSplitResizing] = useState(false)
  const [isMapPanelResizing, setIsMapPanelResizing] = useState(false)
  const [mapReady, setMapReady] = useState(false)
  const [fourthColRightEdge, setFourthColRightEdge] = useState<number>()
  const splitContainerRef = useRef<HTMLDivElement>(null)
  // Where the asset table's header row and right edge sit inside the split container. The
  // detail sheet starts there, so its title bar reads as the table's second header cell.
  const [tableHeader, setTableHeader] = useState<{ top: number; height: number; left: number }>()
  const handleHeaderMeasured = useCallback((rect: { top: number; height: number; right: number }) => {
    const container = splitContainerRef.current
    if (!container) return
    const bounds = container.getBoundingClientRect()
    const next = { top: rect.top - bounds.top, height: rect.height, left: rect.right - bounds.left }
    setTableHeader((prev) =>
      prev?.top === next.top && prev.height === next.height && prev.left === next.left ? prev : next
    )
  }, [])
  const { companies } = useCompany()
  const { configuration, isLoading: isDashboardLoading } = useDashboard()

  const lastFlewToCompanyRef = useRef<number | undefined>(undefined)
  const wasLoadingRef = useRef(false)
  const initialCompanySeededRef = useRef(false)
  // The asset to open from the URL, held until its company's assets have loaded (loading a
  // company clears the selection, so applying it earlier would be undone).
  const pendingAssetLinkRef = useRef<AssetLink | undefined>(parseAssetLink(window.location.hash))

  useEffect(() => {
    if (isDashboardLoading || companies.length === 0) {
      return
    }
    const accessible = (id: number | undefined) => (id && companies.some((c) => c.id === id) ? id : undefined)
    const savedMap = Object.values(configuration.assetMaps).find((map) => map.isDefault)
    const savedMapCompanyId = savedMap ? (savedMap.filters.companyId ?? savedMap.companyId) : undefined
    const savedMapIsUsable = !!savedMap && (!savedMapCompanyId || accessible(savedMapCompanyId) !== undefined)
    if (savedMap && savedMapIsUsable) {
      setSelectedSavedMap(savedMap)
      if (!context.sidebarHydrated) {
        context.setSidebarState({ ...defaultSidebarState, ...savedMap.sidebarState })
      }
      if (!context.mapHydrated) {
        context.setMapState(savedMap.mapState)
      }
      if (!context.filtersHydrated) {
        context.setFilters({
          ...savedMap.filters,
          alarmCodes: new Set(savedMap.filters.alarmCodes as unknown as number[]),
          alerts: new Set(savedMap.filters.alerts as unknown as string[]),
          companyId: savedMapCompanyId,
        })
      }
    }
    const pendingLink = pendingAssetLinkRef.current
    if (pendingLink && !accessible(pendingLink.companyId)) {
      pendingAssetLinkRef.current = undefined
    }
    const companyId =
      (pendingAssetLinkRef.current ? pendingAssetLinkRef.current.companyId : undefined) ??
      accessible(context.filters.companyId) ??
      (savedMapIsUsable ? accessible(savedMapCompanyId) : undefined) ??
      (companies.length === 1 ? companies[0].id : undefined)

    context.setFilters((previous) => (previous.companyId === companyId ? previous : { ...previous, companyId }))
    // Seed the "already framed" company once so we don't override a restored (saved-map or
    // hydrated) viewport on first load — but any company the user picks afterward still flies.
    if (!initialCompanySeededRef.current) {
      lastFlewToCompanyRef.current = companyId
      initialCompanySeededRef.current = true
    }
    context.setIsReady(true)
  }, [isDashboardLoading, configuration.assetMaps, companies, context.setIsReady])

  const mapRef = useRef<MapRef | null>(null)
  // State rather than a ref: changing layouts remounts the map in a new container, and the
  // resize observer below has to follow it. Watching a stale ref left the map unpainted after
  // its container grew back (e.g. closing the detail sheet in the split layout).
  const [mapContainerEl, setMapContainerEl] = useState<HTMLDivElement | null>(null)
  const [isRightClickDragging, setIsRightClickDragging] = useState(false)
  const rightClickStartRef = useRef<{ x: number; y: number; pitch: number; bearing: number } | null>(null)
  const currentAssetIdRef = useRef<number>(undefined)

  /* don't think we need to do this, i was debugging
  useEffect(() => {
    const map = mapRef.current?.getMap()
    if (map) {
      const source = map.getSource('assets-source') as mapboxgl.GeoJSONSource
      if (source) {
        context.filteredAssets.forEach((a) => {
          map.setFeatureState({ source: 'assets', id: a.id }, { selected: false })
        })
      }
    }
  }, [context.filteredAssets])
  */

  const setCurrentAssetId = useCallback((id: number | undefined, _origin: string) => {
    const map = mapRef.current?.getMap()
    if (map && map.isStyleLoaded()) {
      const source = map.getSource('assets-source') as mapboxgl.GeoJSONSource
      if (source) {
        if (currentAssetIdRef.current && currentAssetIdRef.current !== id) {
          map.setFeatureState({ source: 'assets-source', id: currentAssetIdRef.current }, { selected: false })
        }
        /* Don't set this until we've loaded the full asset */
        /* But that causes too big a delay, wish I had said why not to do that */
        /* I think it's because the asset might have moved so when it gets the new
         * coordinates, the current asset jumps. The no selected layer goes away
         * immediately, the selected layer waits until the asset loads so it's blank for a while. */
        if (id) {
          map.setFeatureState({ source: 'assets-source', id }, { selected: true })
        }
      }
    }
    currentAssetIdRef.current = id
    context.setCurrentAssetId(id)
  }, [])

  // Open the asset named in the URL once its company has loaded.
  useEffect(() => {
    const link = pendingAssetLinkRef.current
    if (!link || context.isLoadingAssets || context.filters.companyId !== link.companyId) return
    pendingAssetLinkRef.current = undefined
    setCurrentAssetId(link.assetId, 'urlHash')
    const summary = context.allAssets.find((a) => a.id === link.assetId)
    const map = mapRef.current?.getMap()
    if (map && summary) {
      map.flyTo({ center: [summary.location.longitude, summary.location.latitude], duration: 1000 })
    }
    // Phones have no room for the sheet, as with a map click.
    if (window.matchMedia('(min-width: 768px)').matches) {
      setAssetDetailSheetIsOpen(true)
    } else {
      context.setShowAssetModal(true)
    }
  }, [context.isLoadingAssets, context.filters.companyId, context.allAssets, setCurrentAssetId])

  // Keep the URL naming the asset whose details are open. replaceState, not navigate: opening
  // assets shouldn't pile up history entries, and the router has nothing to re-render.
  const detailIsOpen = assetDetailSheetIsOpen || context.showAssetModal
  useEffect(() => {
    if (pendingAssetLinkRef.current) return
    const hash = formatAssetLink(
      detailIsOpen && context.currentAssetId && context.filters.companyId
        ? { assetId: context.currentAssetId, companyId: context.filters.companyId }
        : undefined
    )
    if (window.location.hash === hash) return
    const { pathname, search } = window.location
    window.history.replaceState(window.history.state, '', `${pathname}${search}${hash}`)
  }, [detailIsOpen, context.currentAssetId, context.filters.companyId])

  // Clear selection when currentAssetId becomes undefined (e.g., from reset)
  useEffect(() => {
    if (context.currentAssetId === undefined && currentAssetIdRef.current !== undefined) {
      const map = mapRef.current?.getMap()
      if (map && map.isStyleLoaded()) {
        const source = map.getSource('assets-source') as mapboxgl.GeoJSONSource
        if (source && currentAssetIdRef.current) {
          map.setFeatureState({ source: 'assets-source', id: currentAssetIdRef.current }, { selected: false })
        }
      }
      currentAssetIdRef.current = undefined
    }
  }, [context.currentAssetId])

  // Fly to bounds when a new company's assets are loaded
  useEffect(() => {
    const map = mapRef.current?.getMap()
    const currentCompany = context.filters.companyId
    const isLoading = context.isLoadingAssets
    const hasAssets = context.allAssets.length > 0

    // Track loading state transitions
    if (isLoading) {
      wasLoadingRef.current = true
    }

    if (!map || !mapReady) return

    // Only fly when loading just completed (was loading, now not loading) and we have assets
    const justFinishedLoading = wasLoadingRef.current && !isLoading
    if (justFinishedLoading && hasAssets && currentCompany) {
      wasLoadingRef.current = false
      // Fly whenever a company we haven't framed yet finishes loading. The initial restored
      // (saved-map / hydrated) company is pre-seeded into the ref so it isn't overridden.
      if (currentCompany !== lastFlewToCompanyRef.current) {
        console.debug(
          '[Map] Flying to bounds for company',
          currentCompany,
          context.mapState.bounds,
          context.mapState.zoom
        )
        lastFlewToCompanyRef.current = currentCompany
        const center = getCenterFromBounds(context.mapState.bounds)
        map.flyTo({
          center: [center.longitude, center.latitude],
          zoom: context.mapState.zoom,
          duration: 1000,
        })
      }
    }
  }, [
    context.filters.companyId,
    context.allAssets.length,
    context.mapState.bounds,
    context.mapState.zoom,
    context.isLoadingAssets,
    mapReady,
  ])

  /*
  // Set the feature-state after we know the full asset is loaded
  // Nah, that causes a delay, handling it by using the summary temporarily
  // until the full asset is loaded
  useEffect(() => {
    const map = mapRef.current?.getMap()
    const { currentAssetId, currentAssetData } = context
    if (!map || !map.isStyleLoaded() || !currentAssetId || currentAssetId !== currentAssetData?.id) {
      return
    }
    if (map.getSource('assets-source')) {
      console.log('setting fs selected true for %o', currentAssetId)
      map.setFeatureState({ source: 'assets-source', id: currentAssetId }, { selected: true })
    }
    return () => {
      if (map && map.isStyleLoaded()) {
        if (map.getSource('assets-source')) {
          map.setFeatureState({ source: 'assets-source', id: currentAssetId }, { selected: false })
        }
      }
    }
  }, [context.currentAssetId, context.currentAssetData])
  */

  const onClusterClick = useCallback((e: MapMouseEvent) => {
    const map = mapRef.current?.getMap()
    if (map && map.isStyleLoaded()) {
      const features = map.queryRenderedFeatures(e.point, { layers: ['clustered-asset-circle'] })
      if (!features?.[0]?.properties?.cluster_id) {
        return
      }
      setCurrentAssetId(undefined, 'onClusterClick')
      const clusterId = features[0].properties.cluster_id
      const source = map.getSource('assets-source') as mapboxgl.GeoJSONSource
      source.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err || !zoom) {
          return
        }
        map.easeTo({
          center: e.lngLat,
          zoom,
        })
      })
    }
  }, [])

  const onPointClick = useCallback((e: MapMouseEvent) => {
    const map = mapRef.current?.getMap()
    if (map) {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ['unclustered-asset-marker'],
      })
      let id
      if (features?.[0]?.properties?.id) {
        id = features[0].properties.id
      } else {
        const currentFeatures = map.queryRenderedFeatures(e.point, {
          layers: ['current-asset-marker'],
        })
        if (!currentFeatures?.[0]?.properties?.id) {
          return
        }
        id = currentFeatures[0].properties.id
      }
      if (id === currentAssetIdRef.current) {
        // Second click on same asset - open the detail sheet beside the asset list. Phones
        // have no room for the sheet, so they still get the modal.
        if (window.matchMedia('(min-width: 768px)').matches) {
          setAssetDetailSheetIsOpen(true)
        } else {
          context.setShowAssetModal(true)
        }
      } else {
        // First click - just select the asset (sidebar will scroll into view)
        setCurrentAssetId(id, 'onPointClick')
      }
    }
  }, [])

  const cursorPointer = useCallback(() => {
    const map = mapRef.current?.getMap()
    if (map) {
      map.getCanvas().style.cursor = 'pointer'
    }
  }, [])

  const cursorNormal = useCallback(() => {
    const map = mapRef.current?.getMap()
    if (map) {
      map.getCanvas().style.cursor = ''
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current?.getMap()
    if (map) {
      map.on('click', 'clustered-asset-circle', onClusterClick)
      map.on('mouseover', 'clustered-asset-circle', cursorPointer)
      map.on('mouseleave', 'clustered-asset-circle', cursorNormal)

      map.on('click', 'unclustered-asset-marker', onPointClick)
      map.on('mouseover', 'unclustered-asset-marker', cursorPointer)
      map.on('mouseleave', 'unclustered-asset-marker', cursorNormal)

      map.on('click', 'current-asset-marker', onPointClick)

      return () => {
        map.off('click', 'clustered-asset-circle', onClusterClick)
        map.off('mouseover', 'clustered-asset-circle', onPointClick)
        map.off('mouseleave', 'clustered-asset-circle', cursorNormal)

        map.off('click', 'unclustered-asset-marker', cursorPointer)
        map.off('mouseover', 'unclustered-asset-marker', cursorPointer)
        map.off('mouseleave', 'unclustered-asset-marker', cursorNormal)

        map.off('click', 'current-asset-marker', onPointClick)
      }
    }
  }, [mapRef.current])

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return
      const newWidth = e.clientX
      const minWidth = 8 // Minimum width for dragger only
      const maxWidth = window.innerWidth * 0.5 // Max 50% of screen width
      context.setSideBarWidth(Math.max(minWidth, Math.min(maxWidth, newWidth)))
    }

    const handleMouseUp = () => {
      setIsResizing(false)
    }

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing])

  useEffect(() => {
    if (!isSplitResizing) return

    const handleMouseMove = (e: MouseEvent) => {
      const container = splitContainerRef.current
      if (!container) return
      const rect = container.getBoundingClientRect()
      const ratio = Math.max(0.2, Math.min(0.8, (e.clientY - rect.top) / rect.height))
      context.setSidebarState((prev) => ({ ...prev, splitRatio: ratio }))
    }

    const handleMouseUp = () => setIsSplitResizing(false)

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    document.body.style.cursor = 'row-resize'
    document.body.style.userSelect = 'none'

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isSplitResizing])

  useEffect(() => {
    if (!isMapPanelResizing) return

    const handleMouseMove = (e: MouseEvent) => {
      const container = splitContainerRef.current
      if (!container) return
      const rect = container.getBoundingClientRect()
      // Never let the list shrink below the always-on asset column (Asset + Alert + Power + Gateway).
      const minListWidth = Math.min(Math.max(200, fourthColRightEdge ?? 0), rect.width - 200)
      const width = Math.max(minListWidth, Math.min(rect.width - 200, e.clientX - rect.left))
      context.setSidebarState((prev) => ({ ...prev, mapPanelWidth: width }))
    }

    const handleMouseUp = () => {
      setIsMapPanelResizing(false)
      const map = mapRef.current?.getMap()
      if (map) setTimeout(() => map.resize(), 50)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isMapPanelResizing, fourthColRightEdge])

  // Trigger map resize when sidebar width, layout mode, or split ratio changes
  useEffect(() => {
    if (mapRef.current?.getMap) {
      const map = mapRef.current.getMap()
      if (map) {
        setTimeout(() => {
          map.resize()
        }, 50)
      }
    }
  }, [context.sidebarState.sidebarWidth, layoutMode, splitRatio, assetDetailSheetIsOpen])

  // Keep the Mapbox canvas in sync with its container size (e.g. when the app nav sidebar
  // collapses/expands), otherwise the map paints blank in the newly available space.
  useEffect(() => {
    const el = mapContainerEl
    if (!el) return
    let raf = 0
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => mapRef.current?.getMap()?.resize())
    })
    observer.observe(el)
    return () => {
      cancelAnimationFrame(raf)
      observer.disconnect()
    }
  }, [mapContainerEl])

  // console.log({ geojsonData: context.geojsonData, vp: context.viewportFilteredAssets })

  const getMapStyleUrl = (): string => {
    if (context.mapState.style === '3d') {
      return 'mapbox://styles/mapbox/standard'
    }
    if (context.mapState.style === 'satellite') {
      return 'mapbox://styles/mapbox/satellite-streets-v12'
    }
    return 'mapbox://styles/mapbox/streets-v9'
  }

  const showSkeleton =
    context.isSwitchingView || (context.isLoadingAssets && context.allAssets.length === 0 && context.filters.companyId)

  const mapArea = (
    <div
      // Split with the detail sheet open: the sheet takes the map's place. The map stays mounted
      // (hidden) so it doesn't reload, and repaints when it shows again.
      className={`relative min-w-0 min-h-0 ${
        layoutMode === 'table' || (layoutMode === 'split' && assetDetailSheetIsOpen)
          ? 'hidden'
          : layoutMode === 'split'
            ? 'w-full'
            : 'flex-1 h-full'
      }`}
      style={layoutMode === 'split' ? { flex: `${splitRatio} 1 0` } : undefined}
      onClick={(e) => {
        // Only close filters if clicking on the map itself, not on filter elements
        const target = e.target as HTMLElement
        const isFilterElement = target.closest('.filter-bar-container') || target.closest('[data-filter-dropdown]')
        if (!isFilterElement) {
          setActiveFilter(undefined)
        }
      }}
    >
      <div className={` ${assetDetailSheetIsOpen ? 'hidden' : 'md:block hidden'}`}>
        <AssetQuickView />
      </div>
      <div
        onContextMenu={(e) => {
          if (context.mapState.style === '3d') {
            e.preventDefault()
          }
        }}
        onMouseDown={(e) => {
          if (context.mapState.style === '3d' && e.button === 2) {
            // Right mouse button
            e.preventDefault()
            setIsRightClickDragging(true)
            const map = mapRef.current?.getMap()
            if (map) {
              const currentPitch = map.getPitch()
              const currentBearing = map.getBearing()
              rightClickStartRef.current = {
                x: e.clientX,
                y: e.clientY,
                pitch: currentPitch,
                bearing: currentBearing,
              }
            }
          }
        }}
        onMouseMove={(e) => {
          if (isRightClickDragging && rightClickStartRef.current && context.mapState.style === '3d') {
            const map = mapRef.current?.getMap()
            if (map) {
              const deltaX = e.clientX - rightClickStartRef.current.x
              const deltaY = e.clientY - rightClickStartRef.current.y

              // Update bearing (rotation around vertical axis) - horizontal movement
              const newBearing = rightClickStartRef.current.bearing + deltaX * 0.5
              // Update pitch (tilt) - vertical movement
              const newPitch = Math.max(0, Math.min(60, rightClickStartRef.current.pitch - deltaY * 0.3))
              context.setMapState((prev) => ({ ...prev, pitch: newPitch, bearing: newBearing }))
              map.setBearing(newBearing)
              map.setPitch(newPitch)
            }
          }
        }}
        onMouseUp={(e) => {
          if (e.button === 2) {
            setIsRightClickDragging(false)
            rightClickStartRef.current = null
          }
        }}
        onMouseLeave={() => {
          setIsRightClickDragging(false)
          rightClickStartRef.current = null
        }}
        className="w-full h-full"
      >
        <div className="h-full flex flex-col">
          <div className="rounded-lg overflow-hidden"></div>
          <div ref={setMapContainerEl} className="flex-1 min-h-0 relative rounded-lg overflow-hidden">
            <MapLayerToggles mapRef={mapRef} />
            <MapStyleToggles mapRef={mapRef} onChangeStyle={() => setMapReady(false)} />
            {showSkeleton && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                <div className="bg-base-100/95 rounded-lg shadow-lg px-4 py-3 flex items-center gap-3">
                  <span className="loading loading-spinner loading-md text-primary" />
                  <span className="text-sm">Loading assets…</span>
                </div>
              </div>
            )}
            <MapboxMap
              key={`${context.mapState.style}-${context.filtersResetCount}`}
              ref={mapRef}
              mapboxAccessToken={mapboxToken}
              initialViewState={{
                ...getCenterFromBounds(context.mapState.bounds),
                zoom: context.mapState.zoom,
                pitch: context.mapState.style === '3d' ? context.mapState.pitch : 0,
                bearing: context.mapState.style === '3d' ? context.mapState.bearing : 0,
              }}
              onLoad={() => {
                // Set initial map bounds on load for viewport filtering
                const map = mapRef.current?.getMap()
                if (map) {
                  const bounds = map.getBounds()
                  if (bounds) {
                    context.setMapState((prev) => ({
                      ...prev,
                      bounds: {
                        north: bounds.getNorth(),
                        south: bounds.getSouth(),
                        east: bounds.getEast(),
                        west: bounds.getWest(),
                      },
                    }))
                  }
                  addMarkerImages(map)
                  addLabelBackgrounds(map)
                  setMapReady(true)
                }
              }}
              style={{ width: '100%', height: '100%' }}
              mapStyle={getMapStyleUrl()}
              onMove={(evt) => {
                const zoom = evt.viewState.zoom || 6
                let bounds = context.mapState.bounds
                const map = mapRef.current?.getMap()
                if (map) {
                  const b = map.getBounds()
                  if (b) {
                    bounds = {
                      north: b.getNorth(),
                      south: b.getSouth(),
                      east: b.getEast(),
                      west: b.getWest(),
                    }
                  }
                }

                const pitch = context.mapState.style === '3d' ? evt.viewState.pitch || 0 : 0
                const bearing = context.mapState.style === '3d' ? evt.viewState.bearing || 0 : 0
                context.setMapState((prev) => ({
                  ...prev,
                  zoom,
                  pitch,
                  bearing,
                  bounds,
                }))
              }}
              onClick={(evt) => {
                if (evt.features && evt.features.length > 0) {
                  return
                }
                if (!context.mapState.focus) {
                  setActiveFilter(undefined)
                  setCurrentAssetId(undefined, 'onMapClick')
                }
                return
              }}
              /*
            onMouseEnter={(evt) => {
              const map = mapRef.current?.getMap()
              if (!map || !evt.point) return

              // Query features at mouse position
              const features = map.queryRenderedFeatures(evt.point, {
                layers: ['unclustered-asset', 'clustered-assets'],
              })

              if (features && features.length > 0) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const feature = features[0] as any
                const props = feature.properties || {}

                // Check if it's a cluster
                if (props.point_count) {
                  const count = props.point_count as number
                  // evt.point is in screen coordinates relative to the map container
                  setHoverPopover({
                    x: evt.point.x,
                    y: evt.point.y - 10, // Offset above the marker
                    content: `${count} asset${count !== 1 ? 's' : ''}`,
                    visible: true,
                  })
                } else if (props.id) {
                  // Individual marker - get asset name
                  const assetId = props.id as number
                  const asset = context.viewportFilteredAssets.find((a) => a.id === assetId)
                  if (asset) {
                    setHoverPopover({
                      x: evt.point.x,
                      y: evt.point.y - 10, // Offset above the marker
                      content: asset.name,
                      visible: true,
                    })
                  }
                }
              }
            }}
            onMouseLeave={() => {
              setHoverPopover(null)
            }}
            */
            >
              <BottomNavMobileControls
                activeFilter={activeFilter}
                setActiveFilter={setActiveFilter}
                setCurrentAssetId={setCurrentAssetId}
                mapRef={mapRef}
              />

              <NavigationControl
                position="bottom-left"
                style={{ display: window.innerWidth < 768 ? 'none' : 'block' }}
              />

              {mapReady && <MapLayers />}
            </MapboxMap>
          </div>
        </div>

        {/* Hover Popover */}
        {/*hoverPopover && hoverPopover.visible && (
            <div
              className="absolute pointer-events-none z-50 bg-base-100 text-base-content px-2 py-1 rounded shadow-lg border border-base-300 text-sm whitespace-nowrap"
              style={{
                left: `${hoverPopover.x}px`,
                top: `${hoverPopover.y}px`,
                transform: 'translate(-50%, -100%)',
              }}
            >
              {hoverPopover.content}
            </div>
          )*/}
      </div>

      {/* <Timeline /> */}
      <Legend />
    </div>
  )

  const assetPanel = (
    <div
      className={`min-w-0 min-h-0 flex flex-col bg-base-100 ${layoutMode === 'split' ? 'w-full' : 'flex-1 h-full'}`}
      style={layoutMode === 'split' ? { flex: `${1 - splitRatio} 1 0` } : undefined}
    >
      <AssetPanel
        mapRef={mapRef}
        setCurrentAssetId={setCurrentAssetId}
        onFourthColRightMeasured={setFourthColRightEdge}
        onHeaderMeasured={handleHeaderMeasured}
      />
    </div>
  )
  /*
  const styles = {
    road: 'Mapbox Streets',
    satellite: 'Mapbox Satellite Streets',
    '3d': 'Mapbox Standard',
  }

  const map: mapboxgl.Map = mapRef.current
  const style = map?.getStyle()
  const currentStyle = style?.name || style?.imports?.[0]?.data?.name
  const isStyleLoaded = map?.isStyleLoaded() && currentStyle === styles[context.mapState.style]
  console.log({
    mapReady,
    tstyle: context.mapState.style,
    currentStyle,
    mapStyle: map?.getStyle(),
    loaded: isStyleLoaded,
  })
  */

  return (
    <div className="flex flex-col min-w-0 overflow-x-hidden h-full w-full relative flex-1">
      <MapToolbar mapRef={mapRef} activeFilter={activeFilter} setActiveFilter={setActiveFilter} />
      <div className="flex flex-1 min-h-0">
        <div
          ref={splitContainerRef}
          className="flex-1 min-w-0 min-h-0
 flex relative flex-row"
        >
          {layoutMode !== 'split' && (
            <div className="">
              <AssetDetailSheet
                variant="overlay"
                isOpen={assetDetailSheetIsOpen}
                handleClose={() => setAssetDetailSheetIsOpen(false)}
                assetId={context.currentAssetId}
                companyId={context.filters.companyId}
                fourthColRightEdge={fourthColRightEdge}
                tableHeader={tableHeader}
              />
            </div>
          )}

          {layoutMode === 'map' && (
            <>
              <div
                className="min-w-0 min-h-0 flex flex-col bg-base-100
 h-full shrink-0"
                style={{ width: `${context.sidebarState.mapPanelWidth ?? defaultSidebarState.mapPanelWidth}px` }}
              >
                <AssetPanel
                  mapRef={mapRef}
                  setCurrentAssetId={setCurrentAssetId}
                  onFourthColRightMeasured={setFourthColRightEdge}
                  onHeaderMeasured={handleHeaderMeasured}
                />
              </div>
              <div
                onMouseDown={(e) => {
                  e.preventDefault()
                  setIsMapPanelResizing(true)
                }}
                className={`w-1.5 h-full cursor-col-resize
 transition-colors shrink-0 ${isMapPanelResizing ? 'bg-primary' : 'bg-base-300 hover:bg-primary/60'}`}
                title="Drag to resize"
              />
            </>
          )}

          {layoutMode === 'split' ? (
            // Map above, list below. With the detail sheet open it takes the map's half.
            <div className="flex flex-col flex-1 min-w-0 min-h-0">
              {assetDetailSheetIsOpen && (
                <div className="min-w-0 min-h-0 w-full" style={{ flex: `${splitRatio} 1 0` }}>
                  <AssetDetailSheet
                    variant="pane"
                    isOpen
                    handleClose={() => setAssetDetailSheetIsOpen(false)}
                    assetId={context.currentAssetId}
                    companyId={context.filters.companyId}
                  />
                </div>
              )}
              {mapArea}
              <div
                onMouseDown={(e) => {
                  e.preventDefault()
                  setIsSplitResizing(true)
                }}
                className={`h-1.5 w-full cursor-row-resize transition-colors shrink-0 ${
                  isSplitResizing ? 'bg-primary' : 'bg-base-300 hover:bg-primary/60'
                }`}
                title="Drag to resize"
              />
              {assetPanel}
            </div>
          ) : (
            <>
              {mapArea}
              {layoutMode === 'table' && assetPanel}
            </>
          )}
        </div>
      </div>

      {/* Phones only: on wider screens the detail sheet takes its place. */}
      <AssetDetailModal
        show={context.showAssetModal}
        onHide={() => {
          context.setShowAssetModal(false)
        }}
        assetId={context.currentAssetId}
        companyId={context.filters.companyId}
      />
    </div>
  )
}
